'use client';

import dynamic from 'next/dynamic';
import InlineStyle from '@/components/InlineStyle';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Check } from 'lucide-react';
import { BookDemoButton } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import { HeroCollage } from '@/components/primitives/HeroCollage';
import meStyles from '@/app/lp/customer-experience-platform-middle-east/me-lp.module.css';
import { ME_HERO_COLLAGE } from '@/app/lp/customer-experience-platform-middle-east/me-hero-collage';
import {
  fadeUp, slideInLeft, slideInRight, scaleIn, viewportOnce,
  spring, springSoft, staggerContainer,
} from '@/lib/motion';

const BookDemoSection = dynamic(() => import('@/components/BookDemoSection'));
const MeAICarousel = dynamic(() => import('@/app/lp/customer-experience-platform-middle-east/MeAICarousel'));
const MeOutcomesCarousel = dynamic(() => import('@/app/lp/customer-experience-platform-middle-east/MeOutcomesCarousel'));
const MeLogoMarquee = dynamic(() => import('@/app/lp/customer-experience-platform-middle-east/MeLogoMarquee'));
const MeTrustBanner = dynamic(() => import('@/app/lp/customer-experience-platform-middle-east/MeTrustBanner'));

/* ── Design tokens ──────────────────────────────────────────────────────── */
const BRAND = '#0098FF';
const BRAND_SUBTLE = 'rgba(0,152,255,0.08)';
const BRAND_BORDER = 'rgba(0,152,255,0.18)';
const BG_DARK = '#0D1B3E';
const IMG = '/wp-content/uploads/me-cx';

/* ── Content data ───────────────────────────────────────────────────────── */
const METRICS = [
  { value: '350+', label: 'Global Brands' },
  { value: '40B+', label: 'Data Points Processed' },
  { value: '20M+', label: 'Customer Cases Solved' },
  { value: '35M+', label: 'Customers Profiled' },
  { value: '4', label: 'Continents Served' },
];

const STEPS = [
  {
    num: '01', title: 'Listen', img: 'step-listen.webp',
    desc: "Track every brand mention across social, review platforms, and news — in Arabic and English — with real-time social listening. If your customers are talking, you'll know instantly.",
  },
  {
    num: '02', title: 'Engage', img: 'step-engage.webp',
    desc: 'Respond faster with AI-powered automation and a 360° view of every customer. Deliver omnichannel customer service that feels personal — because it is. Your team resolves more with less effort.',
  },
  {
    num: '03', title: 'Analyse', img: 'step-analyse.webp',
    desc: "Turn raw data into decisions. Understand voice of customer (VoC) signals, track sentiment trends, and use social media analytics to identify what's working — and what to fix next.",
  },
];

const SMART_CX_FEATURES = [
  { title: 'AI-Guided Agent Assistance', desc: 'Real-time AI copilot that reduces guesswork and keeps CSAT scores high.', icon: 'feature-ai-guided.svg' },
  { title: 'Intelligent Query Prioritisation', desc: 'Surfaces and prioritises interactions by VIP status, urgency, and context—so critical issues are never overlooked.', icon: 'feature-intelligent-query.svg' },
  { title: 'Automated Ticket Summarisation', desc: 'Agents resolve cases at speed without reading full conversation threads.', icon: 'feature-summarisation.svg' },
  { title: 'Workflow Automation', desc: 'Automates FAQs at scale—so your agents can focus on complex and sensitive interactions.', icon: 'feature-automation.svg' },
  { title: 'Seamless Multilingual Support', desc: 'Engage customers in 40+ languages, including Arabic and its regional dialects.', icon: 'feature-multilingual.svg' },
  { title: 'Automated NPS Surveys', desc: 'Capture voice of customer (VoC) feedback the moment it matters.', icon: 'feature-nps-survey.svg' },
];

const COMMAND_CENTRE_FEATURES = [
  { title: 'Live Monitoring Dashboard', desc: 'Real-time dashboards tailored for marketing leaders & teams.', icon: 'feature-ai-guided.svg' },
  { title: 'Crisis Detection & Response', desc: 'Identify crises within 90 seconds and instantly alert legal, PR, or CX teams—enabling rapid response, tighter control, and minimized brand impact.', icon: 'feature-intelligent-query.svg' },
  { title: 'Competitive Intelligence', desc: 'Track competitors, benchmark performance, and find the gaps.', icon: 'feature-summarisation.svg' },
  { title: 'Campaign Optimisation', desc: 'Turn audience signals into quick insights to refine strategy and maximise ROI.', icon: 'feature-automation.svg' },
  { title: 'Multi-Location Management', desc: 'Region-specific insights for every branch or outlet — essential for Middle East multi-site brands.', icon: 'feature-multilingual.svg' },
];

const SOCIAL_LISTENING_FEATURES = [
  { title: 'Full Channel Coverage', desc: 'From discussion forums and news to review sites and social media—capture every mention in real time.', icon: 'feature-ai-guided.svg' },
  { title: 'Intent Labels', desc: 'Classify each mention by purpose: complaint, query, praise, or feedback — automatically.', icon: 'feature-intelligent-query.svg' },
  { title: 'Audience Analysis', desc: 'Understand who your customers are and what drives them.', icon: 'feature-summarisation.svg' },
  { title: 'Influencer Tracking', desc: 'Identify the voices that shape purchasing decisions in your market.', icon: 'feature-automation.svg' },
  { title: 'PR Dashboard', desc: "Your brand's media presence, at a glance.", icon: 'feature-multilingual.svg' },
  { title: 'Performance Intelligence', desc: 'Turn social media analytics into high-impact campaign decisions.', icon: 'feature-nps-survey.svg' },
];

const TABS = [
  {
    key: 'smart-cx',
    label: 'Smart CX Engine',
    heading: 'Smart CX Engine — AI Customer Experience Software That Works',
    lead: 'AI that delivers personalized, human-like customer interactions at scale—enhancing response times while consistently preserving your brand voice.',
    features: SMART_CX_FEATURES,
  },
  {
    key: 'command-centre',
    label: 'Command Centre',
    heading: 'Command Centre — Digital Command Center for Real-Time Brand Intelligence',
    lead: 'Detect and contain crises in real time — protecting your brand\u2019s reputation before issues escalate.',
    features: COMMAND_CENTRE_FEATURES,
  },
  {
    key: 'social-listening',
    label: 'Social Listening Platform',
    heading: 'Social Listening Platform — Always-On Brand Monitoring',
    lead: 'Most platforms stop at standard Arabic. Locobuzz goes further — with an advanced Arabic NLP engine that recognises dialects and cultural nuance, so your brand always sounds local, not foreign.',
    features: SOCIAL_LISTENING_FEATURES,
  },
];

const PUBLISHING = {
  heading: 'Publishing — Multi-Channel Content Management Hub',
  lead: 'Plan, queue, approve, and publish content across all channels and all locations — from one unified dashboard. Track performance daily, weekly, and monthly.',
};

const DIALECTS = ['Gulf Arabic', 'Levantine', 'Egyptian', 'MSA', 'Maghrebi'];

const LANG_BULLETS = [
  'Advanced Arabic NLP engine with dialect recognition',
  'Sentiment analysis tuned for Arabic idiom and cultural expression',
  'Cultural context awareness — not just literal translation',
  'Bilingual (Arabic / English) workflows in one interface',
  'Full RTL support throughout the platform',
];

const AI_FEATURES = [
  { title: 'Location Intelligence', img: 'ai-carousel/location-intelligence.webp', desc: 'Unifies reviews, surveys, and conversations into a single view per location—highlighting issues, enabling context-aware responses, and helping teams act early while maintaining consistency at scale.' },
  { title: 'SignalSense™', img: 'ai-carousel/signalsense.webp', desc: 'Surfaces only high-impact signals—cutting noise and prioritising alerts by severity and reach to the right teams to act faster and seize emerging momentum early.' },
  { title: 'ContextualPulse™', img: 'ai-carousel/contextual.webp', desc: 'Crafts the right response every time — personalised, on-brand, and in the right tone. Equips agents with a strong starting point to respond faster and stay consistent across interactions.' },
  { title: 'ResponseGenie™', img: 'ai-carousel/responsegenie.webp', desc: 'Drafts the right reply every time — personalised, on-brand, and in the right tone. Gives every agent a strong, ready-to-refine starting point for faster, more consistent responses.' },
  { title: 'AgentIQ™', img: 'ai-carousel/agentiq.webp', desc: "Works in real time alongside every agent — catching risky responses before they're sent, reducing reopens, and protecting CSAT." },
  { title: 'Workflow Automation', img: 'ai-carousel/workflow-automation.webp', desc: 'Accelerate support efficiency with DIY workflows that automate responses, assignments, and surveys using intelligent, condition-based triggers.' },
];

const DANUBE_STATS = [
  { icon: 'icon-hourglass-v2.webp', value: '<1 Min', label: 'Average response time across all channels' },
  { icon: 'icon-message-v2.webp', value: '100%', label: 'Automated reply success rate' },
  { icon: 'icon-question-v2.webp', value: 'Zero', label: 'Cold queries left unanswered' },
];

const OUTCOMES = [
  {
    icon: 'icon-heart-v2.webp', metric: '25 → 63%', label: 'response rate uplift',
    tag: 'BUILD ADVOCACY', industry: 'Healthcare',
    heading: 'A leading healthcare network across India',
    vertical: 'Healthcare · 72 hospitals',
    desc: 'Patient experience & reputation across 72 hospitals and 3M+ followers. Sensitive-response with human-in-loop, hospital-level scorecards for leadership.',
    modules: ['ContextualPulse™', 'AgentIQ™', 'ResponseGenie™'],
  },
  {
    icon: 'icon-airline-v2.webp', metric: '40% → 100%', label: 'Faster resolutions, full SLA',
    tag: 'CUT COST-TO-SERVE', industry: 'Airline',
    heading: "A leading full-service carrier in India",
    vertical: "Aviation · India's largest airline",
    desc: 'Centralised email + social with Salesforce CRM integration. AI automation for faster resolutions, real-time alerts for safety-critical escalations.',
    modules: ['Workflow Automation', 'ResponseGenie™', 'AgentIQ™'],
  },
  {
    icon: 'icon-bfsi-v2.webp', metric: '120M', label: 'Conversations monitored a year',
    tag: 'PROTECT AT SCALE', industry: 'BFSI',
    heading: "One of India's largest private sector banks",
    vertical: "BFSI · One of India's largest private sector banks.",
    desc: 'Digital Command Centre with 22+ data-viz screens across 3 HQ locations. Live end-to-end social-operations visibility for senior leadership.',
    modules: ['SignalSense™', 'ContextualPulse™'],
  },
  {
    icon: 'icon-automobile-v2.webp', metric: '–87% → +32%', label: 'Response time ↓, CSAT ↑',
    tag: 'SCALE ACROSS NETWORK', industry: 'Automobile',
    heading: '900+ dealerships across the network',
    vertical: 'Automobile · 900+ dealerships',
    desc: 'GMB + social ORM at scale across 900+ dealer locations. Brand-safe voice, dealer-level MIS, real-time visibility from group HQ to forecourt.',
    modules: ['Workflow Automation', 'AgentIQ™', 'ResponseGenie™'],
  },
];

const PARTNER_POINTS = [
  { title: 'Proven at Scale', desc: "Trusted by 350+ global brands across 4 continents. We've solved the challenges you face, at scale, before." },
  { title: 'Enterprise-Grade Reliability', desc: "Built for brands that can't afford downtime. Enterprise security, scalability, and uptime — so your team can depend on it every single day." },
  { title: 'Always Getting Better', desc: 'AI-first and Microsoft-recognised. New capabilities ship continuously. Our customers benefit first.' },
];

/* ── Page ───────────────────────────────────────────────────────────────── */
export default function MiddleEastCXBody() {
  const [activeTab, setActiveTab] = useState('smart-cx');
  const tab = TABS.find((t) => t.key === activeTab) ?? TABS[0];

  return (
    <>
      {/* ── 1 · HERO (light, centered) ──
          Reveal is CSS-only: framer-motion would ship `opacity: 0` in the
          static HTML and hold the LCP text unpainted until hydration. ── */}
      <section className="me-hero-light">
        <div className="me-hero-light-glow" aria-hidden="true" />
        <div className="me-hero-light-inner">
          <div className="me-hero-reveal">
            <span className="me-eyebrow">
              <MapPin size={13} /> Middle East &amp; MENA
            </span>
          </div>

          <h1 className="me-hero-light-title me-hero-reveal me-hero-reveal--1">
            <span className="me-hero-title-line">The Customer Experience Management</span>
            <span className="me-hero-title-line">
              Platform the{' '}
              <span className="serif-accent italic" style={{ color: BRAND }}>Middle East Trusts</span>
            </span>
          </h1>

          <p className="me-hero-light-lead me-hero-reveal me-hero-reveal--2">
            Listen to every customer, on every channel — in 40+ languages and regional dialects.
            One unified CX platform that helps brands respond faster, resolve smarter, and grow
            stronger—no matter where their customers are.
          </p>

          <div className="me-hero-light-cta me-hero-reveal me-hero-reveal--3">
            <BookDemoButton size="lg" />
          </div>
        </div>

        <div className="me-hero-light-visual">
          <HeroCollage items={ME_HERO_COLLAGE} className="me-hero-collage" />
        </div>
      </section>

      {/* ── 2 · TRUSTED BY + LOGOS ── */}
      <section className={meStyles.trustSection}>
        <p className={meStyles.trustHeadline}>Trusted by 350+ companies worldwide</p>
        <MeLogoMarquee />
      </section>

      {/* ── 3 · METRICS ── */}
      <MetricsBand items={METRICS} />

      {/* ── 4 · THREE STEPS (text top, image bottom) ── */}
      <section style={{ padding: '88px 24px', background: 'var(--bg-page)' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto' }}>
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
            style={{ textAlign: 'center', marginBottom: 56, maxWidth: 760, marginLeft: 'auto', marginRight: 'auto' }}
          >
            <h2 style={{ fontSize: 'clamp(1.6rem,3.2vw,2.4rem)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.25, marginBottom: 16 }}>
              From Raw Mentions to Meaningful Customer Relationships — In Real-time
            </h2>
            <p style={{ fontSize: 17, color: 'var(--text-muted)', lineHeight: 1.7 }}>
              You don&apos;t need a dozen disconnected tools. Locobuzz is a unified customer experience
              platform — so your team spends less time switching screens and more time building relationships.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer(0.1)} initial="hidden" whileInView="show" viewport={viewportOnce}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 28 }}
          >
            {STEPS.map(({ num, title, img, desc }) => (
              <motion.div
                key={num}
                variants={scaleIn} transition={springSoft}
                style={{
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 20, overflow: 'hidden', display: 'flex', flexDirection: 'column',
                }}
              >
                <div style={{ padding: '28px 26px', flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: BRAND }}>{num}</span>
                    <h3 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{title}</h3>
                  </div>
                  <p style={{ fontSize: 15, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{desc}</p>
                </div>
                <img
                  src={`${IMG}/${img}`}
                  alt={`${title} — Locobuzz CX platform`}
                  loading="lazy"
                  decoding="async"
                  style={{ width: '100%', height: 200, objectFit: 'contain', objectPosition: 'center', display: 'block', background: 'var(--bg-page)' }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── 5 · BLUE UNIFIED PLATFORM BAND ── */}
      <section className="me-blue-band-section">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
          className="me-blue-band"
        >
          <div className="me-blue-band-visual">
            <img
              src={`${IMG}/unified-platform-ui.webp`}
              alt="Arabic compliance guard blocking an off-brand customer response"
              loading="lazy"
              decoding="async"
              className="me-blue-band-img"
            />
          </div>
          <div className="me-blue-band-copy">
            <h2 style={{ fontSize: 'clamp(1.6rem,3.2vw,2.3rem)', fontWeight: 700, color: '#fff', lineHeight: 1.28, marginBottom: 18 }}>
              One Customer Experience Management Software — Every customer-facing function.
            </h2>
            <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, margin: 0 }}>
              All your customer interactions, unified in a single platform. Locobuzz brings social listening,
              brand monitoring, customer engagement, reputation management and AI together in a single CX
              platform — built to scale.
            </p>
          </div>
        </motion.div>
      </section>

      {/* ── 6 · SMART CX ENGINE (underline tabs) ── */}
      <section style={{ padding: '88px 24px 0', background: 'var(--bg-card)' }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          <div className="me-tab-bar" role="tablist">
            {TABS.map((t) => {
              const active = t.key === activeTab;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(t.key)}
                  className={`me-tab${active ? ' me-tab--active' : ''}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <motion.div
            key={tab.key}
            variants={fadeUp} initial="hidden" animate="show" transition={spring}
            style={{ textAlign: 'center', marginBottom: tab.features.length ? 44 : 0, maxWidth: 760, marginLeft: 'auto', marginRight: 'auto' }}
          >
            <h2 style={{ fontSize: 'clamp(1.5rem,3vw,2.2rem)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.28, marginBottom: 16 }}>
              {tab.heading}
            </h2>
            <p style={{ fontSize: 17, color: 'var(--text-muted)', lineHeight: 1.7 }}>{tab.lead}</p>
          </motion.div>

          {tab.features.length > 0 && (
            <motion.div
              key={`${tab.key}-features`}
              variants={staggerContainer(0.06)} initial="hidden" animate="show"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}
            >
              {tab.features.map((f) => (
                <motion.div
                  key={f.title}
                  variants={scaleIn} transition={springSoft}
                  style={{
                    background: 'var(--bg-page)', border: '1px solid var(--border)',
                    borderRadius: 16, padding: '24px 22px', position: 'relative',
                  }}
                >
                  {f.icon && (
                    <img
                      src={`${IMG}/${f.icon}`}
                      alt=""
                      aria-hidden="true"
                      style={{ position: 'absolute', top: 20, insetInlineEnd: 20, width: 28, height: 28 }}
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6, paddingInlineEnd: 36 }}>{f.title}</h3>
                  <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* ── 6b · PUBLISHING ── */}
      <section className="me-publishing-section">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
          className="me-publishing-inner"
        >
          <h2 className="me-publishing-heading">{PUBLISHING.heading}</h2>
          <p className="me-publishing-lead">{PUBLISHING.lead}</p>
        </motion.div>
      </section>

      {/* ── 7 · LANGUAGE & DIALECT (dark, full-bleed) ── */}
      <section className="me-dark-band-section">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
          className="me-dark-band"
        >
          <div className="me-dark-band-visual">
            <img
              src={`${IMG}/language-dialect-v2.webp`}
              alt="Arabic social listening dashboard with dialect-aware mention insights"
              loading="lazy"
              decoding="async"
              className="me-dark-band-img"
            />
          </div>
          <div className="me-dark-band-copy">
            <h2 style={{ fontSize: 'clamp(1.5rem,3vw,2.2rem)', fontWeight: 700, color: '#fff', lineHeight: 1.28, marginBottom: 16 }}>
              Understand and Engage Customers in Their Language and Dialect
            </h2>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.78)', lineHeight: 1.7, marginBottom: 24 }}>
              Standard Arabic support is table stakes. Locobuzz goes beyond MSA to understand conversations
              across Gulf, Levantine, Egyptian, and Maghrebi dialects—so no mention is misread and no
              sentiment is missed.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {LANG_BULLETS.map((b) => (
                <li key={b} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 15, color: 'rgba(255,255,255,0.82)', lineHeight: 1.55 }}>
                  <Check size={16} style={{ color: BRAND, flexShrink: 0, marginTop: 3 }} />
                  {b}
                </li>
              ))}
            </ul>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 12 }}>
                Dialect Coverage
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {DIALECTS.map((d) => (
                  <span key={d} style={{
                    padding: '7px 16px', borderRadius: 100, fontSize: 13, fontWeight: 600,
                    background: 'rgba(0,152,255,0.12)', border: `1px solid rgba(0,152,255,0.28)`, color: BRAND,
                  }}>{d}</span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── 8 · AI FEATURES CAROUSEL ── */}
      <MeAICarousel
        features={AI_FEATURES}
        imgBase={IMG}
        flushTop
      />

      {/* ── 9 · DANUBE / CONVERSION BAND ── */}
      <section className="me-conversion-band">
        <div className="me-conversion-inner">
          <motion.div
            variants={slideInLeft} initial="hidden" whileInView="show" viewport={viewportOnce} transition={spring}
            className="me-conversion-stats"
          >
            {DANUBE_STATS.map((s) => (
              <div key={s.label} className="me-conversion-stat">
                <img src={`${IMG}/${s.icon}`} alt="" loading="lazy" decoding="async" style={{ width: 44, height: 44, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 30, fontWeight: 800, color: BRAND, lineHeight: 1.1 }}>{s.value}</div>
                  <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)', lineHeight: 1.4 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </motion.div>

          <motion.div variants={slideInRight} initial="hidden" whileInView="show" viewport={viewportOnce} transition={springSoft} className="me-conversion-copy">
            <img
              src={`${IMG}/danube-properties.svg`}
              alt="Danube Properties"
              loading="lazy"
              decoding="async"
              style={{ height: 40, width: 'auto', marginBottom: 24 }}
            />
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: BRAND, marginBottom: 8 }}>
              (Local Success Story)
            </div>
            <h2 style={{ fontSize: 'clamp(1.5rem,3vw,2.2rem)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.28, marginBottom: 20 }}>
              Where Conversations Turn into Conversions
            </h2>
            <p style={{ fontSize: 18, color: 'var(--text-secondary)', lineHeight: 1.7, fontStyle: 'italic', marginBottom: 16 }}>
              &ldquo;We needed to respond faster across social media without missing a single lead. Locobuzz
              transformed how we handle customer conversations in the real estate sector.&rdquo;
            </p>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)' }}>
              Danube Properties | Real Estate | United Arab Emirates
            </div>
            <img
              src={`${IMG}/danube-house-3d.webp`}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="me-conversion-house"
            />
          </motion.div>
        </div>
      </section>

      {/* ── 10 · OUTCOMES CAROUSEL ── */}
      <MeOutcomesCarousel
        outcomes={OUTCOMES}
        imgBase={IMG}
        title="Four outcomes. Four named customers. Real numbers."
      />

      {/* ── 11 · PARTNER (Figma trust banner + 3 cards) ── */}
      <section className="me-partner-section">
        <div style={{ maxWidth: 1120, margin: '0 auto', position: 'relative' }}>
          <MeTrustBanner imgBase={IMG} />

          <motion.div
            variants={staggerContainer(0.1)} initial="hidden" whileInView="show" viewport={viewportOnce}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}
          >
            {PARTNER_POINTS.map((p) => (
              <motion.div
                key={p.title}
                variants={scaleIn} transition={springSoft}
                style={{
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 18, padding: '30px 26px',
                }}
              >
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>{p.title}</h3>
                <p style={{ fontSize: 15, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{p.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── 12 · FINAL CTA ── */}
      <BookDemoSection formEager={false} />

      <section style={{ padding: '40px 24px 64px', background: 'var(--bg-page)', textAlign: 'center' }}>
        <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)' }}>
          #ShapingExperiences, one interaction at a time&trade;
        </p>
      </section>

      <InlineStyle css={`
        .me-hero-light {
          position: relative;
          overflow: visible;
          padding: 96px 24px 160px;
          background-color: var(--bg-page);
          background-image:
            radial-gradient(circle at 1px 1px, rgba(0, 152, 255, 0.07) 1px, transparent 0);
          background-size: 28px 28px;
          text-align: center;
        }
        .me-hero-light-glow {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 50% 40% at 15% 10%, rgba(0,152,255,0.12) 0%, transparent 70%),
            radial-gradient(ellipse 50% 40% at 85% 10%, rgba(0,152,255,0.10) 0%, transparent 70%);
          pointer-events: none;
        }
        .me-hero-light-inner {
          position: relative;
          max-width: var(--max-content);
          margin: 0 auto;
        }
        .me-hero-reveal {
          animation: meHeroReveal 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .me-hero-reveal--1 { animation-delay: 0.04s; }
        .me-hero-reveal--2 { animation-delay: 0.08s; }
        .me-hero-reveal--3 { animation-delay: 0.12s; }
        @keyframes meHeroReveal {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .me-hero-reveal { animation: none; }
        }
        .me-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: ${BRAND_SUBTLE};
          border: 1px solid ${BRAND_BORDER};
          color: ${BRAND};
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          padding: 6px 16px;
          border-radius: 100px;
          margin-bottom: 24px;
        }
        .me-hero-light-title {
          font-size: clamp(2rem,4.4vw,3.1rem);
          font-weight: 800;
          color: var(--text-primary);
          line-height: 1.15;
          margin-bottom: 18px;
        }
        .me-hero-title-line {
          display: block;
        }
        .me-hero-light-lead {
          font-size: 18px;
          color: var(--text-muted);
          line-height: 1.6;
          margin-bottom: 32px;
          max-width: 640px;
          margin-left: auto;
          margin-right: auto;
        }
        .me-hero-light-cta { margin-bottom: 0; }
        .me-hero-light-visual {
          position: relative;
          width: min(var(--max-wide), calc(100% - 48px));
          max-width: none;
          margin: 48px auto 0;
          padding-bottom: 80px;
          /* Reserve collage height so late image decode does not shift the page */
          min-height: clamp(180px, 28vw, 320px);
        }
        .me-hero-collage {
          width: 100%;
          max-width: none;
          margin: 0 auto;
        }
        .me-hero-collage [data-piece="dashboard"] {
          overflow: hidden;
          border-radius: 14px;
          background: var(--bg-page);
          box-shadow: 0 14px 32px rgba(15, 23, 42, 0.10);
        }
        .me-hero-collage [data-piece="dashboard"] img {
          width: 101%;
          height: auto;
          margin: -0.5% 0 0 -0.5%;
          filter: none;
        }
        @media (max-width: 900px) {
          .me-hero-light-visual {
            width: calc(100% - 32px);
            margin-top: 32px;
            padding-bottom: 24px;
            min-height: 0;
          }
        }
        .me-blue-band-section {
          padding: 0 0 88px;
          background: var(--bg-page);
        }
        .me-blue-band {
          display: grid;
          grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
          gap: clamp(12px, 2vw, 24px);
          align-items: center;
          width: 100%;
          margin: 0;
          padding: clamp(56px, 7vw, 88px) clamp(24px, 6vw, 96px);
          border-radius: 0;
          background: ${BRAND};
        }
        .me-blue-band-visual {
          display: flex;
          justify-content: flex-end;
          align-items: center;
        }
        .me-blue-band-img {
          width: 100%;
          max-width: min(760px, 100%);
          height: auto;
          display: block;
        }
        .me-blue-band-copy {
          max-width: 520px;
        }
        .me-dark-band-section {
          padding: 0;
          background: var(--bg-card);
        }
        .me-dark-band {
          display: grid;
          grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
          gap: clamp(12px, 2vw, 24px);
          align-items: center;
          width: 100%;
          margin: 0;
          padding: clamp(56px, 7vw, 88px) clamp(24px, 6vw, 96px);
          border-radius: 0;
          background: ${BG_DARK};
        }
        .me-dark-band-visual {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          overflow: visible;
        }
        .me-dark-band-img {
          width: 100%;
          max-width: min(760px, 100%);
          height: auto;
          display: block;
          border-radius: 0;
        }
        .me-dark-band-copy {
          max-width: 520px;
        }
        .me-tab-bar {
          display: flex;
          flex-wrap: wrap;
          gap: 0;
          justify-content: center;
          border-bottom: 1px solid var(--border);
          margin-bottom: 44px;
        }
        .me-tab {
          padding: 12px 24px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          color: var(--text-muted);
          transition: color 0.2s, border-color 0.2s;
          margin-bottom: -1px;
        }
        .me-tab--active {
          /* --brand on white is only 3:1; the darker token clears 4.5:1 for text */
          color: var(--brand-dark);
          border-bottom-color: ${BRAND};
        }
        .me-publishing-section {
          padding: 28px 24px 88px;
          background: var(--bg-card);
        }
        .me-publishing-inner {
          max-width: 820px;
          margin: 0 auto;
          text-align: center;
          padding: clamp(40px, 5vw, 56px) clamp(28px, 4vw, 48px);
          border-radius: 24px;
          background: var(--bg-page);
          border: 1px solid var(--border);
          box-shadow: 0 8px 32px rgba(15, 23, 42, 0.04);
        }
        .me-publishing-heading {
          font-size: clamp(1.5rem, 3vw, 2.2rem);
          font-weight: 700;
          color: var(--text-primary);
          line-height: 1.28;
          margin: 0 0 16px;
        }
        .me-publishing-lead {
          font-size: 17px;
          color: var(--text-muted);
          line-height: 1.7;
          margin: 0;
          max-width: 680px;
          margin-inline: auto;
        }
        .me-conversion-band {
          padding: 88px 24px;
          background: linear-gradient(135deg, rgba(236,72,153,0.08) 0%, rgba(0,152,255,0.10) 100%);
        }
        .me-conversion-inner {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 48px;
          align-items: center;
          max-width: 1120px;
          margin: 0 auto;
        }
        .me-conversion-stats {
          display: flex;
          flex-direction: column;
          gap: 16px;
          background: ${BG_DARK};
          border-radius: 24px;
          padding: 32px 28px;
        }
        .me-conversion-stat {
          display: flex;
          align-items: center;
          gap: 18px;
        }
        .me-conversion-copy { position: relative; }
        .me-conversion-house {
          position: absolute;
          bottom: -20px;
          insetInlineEnd: -10px;
          width: 120px;
          height: auto;
          opacity: 0.15;
          pointer-events: none;
        }
        .me-partner-section {
          padding: 88px 24px;
          background: var(--surface-sectionAlt, #F4F6FD);
          position: relative;
          overflow: hidden;
        }
        @media (max-width: 880px) {
          .me-blue-band,
          .me-dark-band,
          .me-conversion-inner { grid-template-columns: 1fr; gap: 32px; }
          .me-conversion-house { display: none; }
        }
      `} />
    </>
  );
}
